
import { useEffect, useLayoutEffect, useState, RefObject } from 'react';

type HeightMap = Record<string, number>;

let sharedHeights: HeightMap = {};
let listeners: ((heights: HeightMap) => void)[] = [];

const notify = () => {
  const snapshot = { ...sharedHeights };
  listeners.forEach(cb => cb(snapshot));
};

const setSharedHeight = (id: string, height: number) => {
  if (sharedHeights[id] !== height) {
    sharedHeights[id] = height;
    notify();
  }
};

const clearSharedHeight = (id: string) => {
  if (id in sharedHeights) {
    delete sharedHeights[id];
    notify();
  }
};

const subscribeToHeights = (callback: (heights: HeightMap) => void) => {
  listeners.push(callback);
  callback({ ...sharedHeights });
  return () => {
    listeners = listeners.filter(cb => cb !== callback);
  };
};


const useSticky = (idOrIds?: string | string[], ref?: RefObject<HTMLElement | null>) => {
  const [height, setHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (!ref || typeof idOrIds !== 'string') return;

    const id = idOrIds;
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const measure = () => {
      const h = ref.current?.getBoundingClientRect().height ?? 0;
      setHeight(h);
      setSharedHeight(id, h);
    };

    measure();

    if (ref.current) {
      resizeObserver = new ResizeObserver(() => {
        measure();
      });
      resizeObserver.observe(ref.current);
    }

    mutationObserver = new MutationObserver(() => {
      const exists = !!ref.current;
      if (!exists) {
        setHeight(0);
        setSharedHeight(id, 0);
      } else {
        measure();
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      clearSharedHeight(id);
    };
  }, [ref, idOrIds]);

  useEffect(() => {
    if (ref) return; 

    const unsubscribe = subscribeToHeights((heights) => {
      if (Array.isArray(idOrIds)) {
        const total = idOrIds.reduce((sum, id) => sum + (heights[id] || 0), 0);
        setHeight(total);
      } else if (typeof idOrIds === 'string') {
        setHeight(heights[idOrIds] || 0);
      } else {
        setHeight(0);
      }
    });

    return unsubscribe;
  }, [ref, idOrIds]);

  return height;
};

export default useSticky;
export { setSharedHeight, clearSharedHeight, subscribeToHeights };
